import React, { useEffect, useState } from 'react';
import { getStoredUser } from '../utils/authUser';
import {
  ANALYTICS_PREFERENCE_EVENT,
  disableAnalytics,
  enableAnalytics,
  getAnalyticsPrivacyState,
  hasSeenAnalyticsNotice,
  initializeAnalytics,
  markAnalyticsNoticeSeen,
  syncStoredAnalyticsUser
} from '../services/analyticsService';
import './AnalyticsPrivacyControls.css';
import { OPEN_PRIVACY_CHOICES_EVENT } from './PrivacyChoicesLink';

const AnalyticsPrivacyControls = () => {
  const [privacyState, setPrivacyState] = useState(getAnalyticsPrivacyState);
  const [showNotice, setShowNotice] = useState(() => !hasSeenAnalyticsNotice());
  const [showChoices, setShowChoices] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const openChoices = () => setShowChoices(true);
    window.addEventListener(OPEN_PRIVACY_CHOICES_EVENT, openChoices);
    return () => window.removeEventListener(OPEN_PRIVACY_CHOICES_EVENT, openChoices);
  }, []);

  useEffect(() => {
    const refreshState = () => setPrivacyState(getAnalyticsPrivacyState());
    window.addEventListener(ANALYTICS_PREFERENCE_EVENT, refreshState);
    initializeAnalytics();
    const storedUser = getStoredUser();
    if (getAnalyticsPrivacyState().gpc) disableAnalytics(storedUser);
    else syncStoredAnalyticsUser(storedUser);
    return () => window.removeEventListener(ANALYTICS_PREFERENCE_EVENT, refreshState);
  }, []);

  useEffect(() => {if(!showNotice || showChoices)return undefined;const timer=setTimeout(()=>{markAnalyticsNoticeSeen();setShowNotice(false);},15000);return()=>clearTimeout(timer);},[showNotice,showChoices]);

  const dismissNotice = () => {
    markAnalyticsNoticeSeen();
    setShowNotice(false);
  };

  const handleDisable = async () => {
    setSaving(true);
    await disableAnalytics(getStoredUser());
    setPrivacyState(getAnalyticsPrivacyState());
    setShowNotice(false);
    setSaving(false);
  };

  const handleEnable = async () => {
    setSaving(true);
    await enableAnalytics(getStoredUser());
    setPrivacyState(getAnalyticsPrivacyState());
    setSaving(false);
  };

  return (
    <>
      {showNotice && (
        <aside className="analytics-notice" aria-label="Analytics notice">
          <div>
            <strong>Privacy & analytics</strong>
            <p>{privacyState.gpc
              ? 'Your browser privacy signal is active, so Google Analytics is disabled.'
              : 'We use analytics to improve your home search. You can opt out anytime in Privacy choices.'}</p>
            <a href="/privacy-policy">Learn more</a>
          </div>
          <div className="analytics-notice__actions">
            <button type="button" className="analytics-notice__dismiss" onClick={dismissNotice}>Got it</button>
            {!privacyState.gpc && privacyState.enabled && (
              <button type="button" className="analytics-notice__opt-out" onClick={handleDisable} disabled={saving}>
                Opt out of Google Analytics
              </button>
            )}
          </div>
        </aside>
      )}

      <button type="button" className="analytics-choices-trigger" onClick={() => setShowChoices(true)} aria-haspopup="dialog">
        Privacy choices
      </button>

      {showChoices && (
        <div className="analytics-choices-backdrop" onMouseDown={() => setShowChoices(false)}>
          <section className="analytics-choices-dialog" role="dialog" aria-modal="true" aria-labelledby="analytics-choices-title" onMouseDown={event => event.stopPropagation()}>
            <button type="button" className="analytics-choices-dialog__close" onClick={() => setShowChoices(false)} aria-label="Close privacy choices">×</button>
            <span className="analytics-choices-dialog__eyebrow">Privacy controls</span>
            <h2 id="analytics-choices-title">Google Analytics</h2>
            <p>We use anonymous browser and account identifiers to understand which pages are useful. We do not add your name, email, phone number, or IP address as Google Analytics identity data.</p>
            <div className="analytics-choices-dialog__status">
              <span>Status</span>
              <strong className={privacyState.enabled ? 'is-enabled' : 'is-disabled'}>{privacyState.enabled ? 'Enabled' : 'Disabled'}</strong>
            </div>
            {privacyState.gpc && <p className="analytics-choices-dialog__gpc">Analytics is disabled because your browser is sending Global Privacy Control. Turn off that browser setting before enabling analytics here.</p>}
            <div className="analytics-choices-dialog__actions">
              {privacyState.enabled ? (
                <button type="button" onClick={handleDisable} disabled={saving}>{saving ? 'Saving…' : 'Disable analytics'}</button>
              ) : (
                <button type="button" onClick={handleEnable} disabled={saving || privacyState.gpc}>{saving ? 'Saving…' : 'Enable analytics'}</button>
              )}
              <a href="/privacy-policy">Read our privacy policy</a>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default AnalyticsPrivacyControls;
